import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type SearchbarHomepageProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

type MedicineSuggestion = {
  Name: string;
  Manufacturer: string;
  Salt_Composition: string;
  Quantity: string | number;
  quantity_type?: string;
  tablets_per_strip?: number;
  fragmented_tablets?: number;
  key: string;
};

const SEARCH_DEBOUNCE_MS = 350;

// Move pure function outside component to avoid recreation
const normalizeQtyType = (qtyType?: string): string => {
  const value = (qtyType || '').toLowerCase();
  switch (value) {
    case 'strip':
    case 'strips':
      return 'Strip';
    case 'bottle':
    case 'bottles':
      return 'Bottles';
    case 'tablet':
    case 'tablets':
    case 'individual':
      return 'Tablet';
    case 'box':
    case 'boxes':
      return 'Box';
    case 'tube':
    case 'tubes':
      return 'Tube';
    default:
      return 'Strip';
  }
};

const SearchbarHomepage: React.FC<SearchbarHomepageProps> = React.memo(({
  value,
  onChange,
  placeholder = 'Search the stock availability here by searching'
}) => {
  // Memoize window size checks to avoid recalculation on every render
  const isMobile = useMemo(() => typeof window !== 'undefined' && window.innerWidth < 768, []);
  const isSmall = useMemo(() => typeof window !== 'undefined' && window.innerWidth < 480, []);

  const [suggestions, setSuggestions] = useState<MedicineSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        console.log('🖱️ Click outside detected, closing dropdown');
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (typeof window === 'undefined') {
      return [];
    }

    const authToken = window.localStorage?.getItem('authToken');
    if (!authToken) {
      setErrorMessage('Authentication token missing. Please log in again.');
      return [];
    }

    try {
      const apiUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/search?q=${encodeURIComponent(query)}&limit=10`;
      console.log('🌐 Making API request to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          Authorization: `Bearer ${authToken}`
        }
      });

      console.log('📡 Response status:', response.status, response.statusText);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Inventory search failed:', response.status, errorText);
        setErrorMessage('Unable to fetch inventory right now.');
        return [];
      }

      const data = await response.json();
      
      // Print full API response to console
      console.log('========================================');
      console.log('📡 API RESPONSE - Full Response Object:');
      console.log('========================================');
      console.log(JSON.stringify(data, null, 2));
      console.log('========================================');
      console.log('🔍 API Response (object):', data);
      console.log('🔍 API Response type:', typeof data);
      console.log('🔍 API Response keys:', data ? Object.keys(data) : 'null/undefined');
      console.log('========================================');
      
      let medicines: any[] = [];
      if (data.inventory && Array.isArray(data.inventory)) {
        medicines = data.inventory;
      } else if (Array.isArray(data)) {
        medicines = data;
      } else if (data.medicines && Array.isArray(data.medicines)) {
        medicines = data.medicines;
      } else if (data.results && Array.isArray(data.results)) {
        medicines = data.results;
      } else if (data.data && Array.isArray(data.data)) {
        medicines = data.data;
      } else if (data.items && Array.isArray(data.items)) {
        medicines = data.items;
      } else if (data && typeof data === 'object') {
        // Try to find any array property in the response
        const arrayKeys = Object.keys(data).filter(key => Array.isArray(data[key]));
        if (arrayKeys.length > 0) {
          console.log('⚠️ Found array properties:', arrayKeys);
          medicines = data[arrayKeys[0]]; // Use the first array found
        }
      }

      console.log('📋 Extracted medicines array:', medicines);
      console.log('📋 Medicines count:', medicines.length);

      if (medicines.length > 0) {
        console.log('🔍 First medicine structure:', medicines[0]);
        console.log('🔍 First medicine keys:', Object.keys(medicines[0]));
        console.log('🔍 First medicine full object:', JSON.stringify(medicines[0], null, 2));
      }

      if (medicines.length === 0 && data) {
        console.warn('⚠️ No medicines found in response. Full response structure:', JSON.stringify(data, null, 2));
      }

      setErrorMessage(null);

      const mappedMedicines = medicines.map((medicine: any) => {
        // Prioritize exact field names from API response (Name, Manufacturer, etc.)
        const mapped = {
          Name: medicine.Name || medicine.medicine_name || medicine.name || medicine.medicineName || '',
          Manufacturer: medicine.Manufacturer || medicine.medicine_company || medicine.manufacturer || medicine.company || '',
          Salt_Composition: medicine.Salt_Composition || medicine.medicine_composition || medicine.salt_composition || medicine.composition || '',
          Quantity: medicine.Quantity || medicine.quantity || medicine.qty || medicine.stock || '',
          quantity_type: medicine.quantity_type || medicine.quantityType || medicine.qty_type || 'Strip',
          tablets_per_strip: medicine.tablets_per_strip || medicine.tabletsPerStrip || 0,
          fragmented_tablets: medicine.fragmented_tablets ?? medicine.fragmentedTablets ?? 0,
          key: String(medicine.key || medicine.id || medicine._id || `medicine-${medicine.Name || medicine.medicine_name || medicine.name || Date.now()}-${Math.random()}`)
        };
        console.log('🔍 Mapped medicine:', mapped);
        return mapped;
      }) as MedicineSuggestion[];

      console.log('✅ Final mapped medicines:', mappedMedicines);
      console.log('✅ Final mapped medicines count:', mappedMedicines.length);
      if (mappedMedicines.length > 0) {
        console.log('✅ First mapped medicine example:', JSON.stringify(mappedMedicines[0], null, 2));
      }
      return mappedMedicines;
    } catch (error) {
      console.error('Error searching inventory:', error);
      setErrorMessage('Something went wrong while searching.');
      return [];
    }
  }, []);

  const handleInputChange = useCallback((nextValue: string) => {
    onChange(nextValue);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!nextValue.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      setIsSearching(false);
      setErrorMessage(null);
      return;
    }

    console.log('⌨️ Input changed, starting search for:', nextValue);
    setIsSearching(true);
    setShowDropdown(true);
    console.log('⌨️ Set isSearching=true, showDropdown=true');

    debounceRef.current = setTimeout(async () => {
      const results = await fetchSuggestions(nextValue);
      console.log('📦 Setting suggestions:', results);
      console.log('📦 Suggestions count:', results.length);
      setSuggestions(results);
      setIsSearching(false);
      // Always show dropdown after search completes (it will show "No medicines found" if empty)
      setShowDropdown(true);
      console.log('📦 Dropdown state after setting suggestions:', {
        showDropdown: true,
        suggestionsCount: results.length,
        isSearching: false
      });
    }, SEARCH_DEBOUNCE_MS);
  }, [fetchSuggestions, onChange]);

  const handleSuggestionClick = useCallback((suggestion: MedicineSuggestion) => {
    onChange(suggestion.Name);
    setShowDropdown(false);
  }, [onChange]);

  const shouldShowDropdown = useMemo(() => {
    // Show dropdown if we're searching, have suggestions, or have an error
    const shouldShow = showDropdown && (isSearching || suggestions.length > 0 || !!errorMessage);
    console.log('🔽 Dropdown visibility check:', {
      showDropdown,
      isSearching,
      suggestionsCount: suggestions.length,
      errorMessage: !!errorMessage,
      shouldShow,
      suggestionsArray: suggestions
    });
    return shouldShow;
  }, [showDropdown, isSearching, suggestions, errorMessage]);

  // Memoize style objects to avoid recreation
  const containerStyle = useMemo(() => ({
    position: 'relative' as const,
    maxWidth: isMobile ? '100%' : '600px',
    margin: '0 auto',
    padding: isSmall ? '0 0.5rem' : isMobile ? '0 0.75rem' : '0',
    width: '100%',
    boxSizing: 'border-box' as const,
    overflow: 'visible' as const // Changed to visible so dropdown isn't clipped
  }), [isMobile, isSmall]);

  const iconContainerStyle = useMemo(() => ({
    position: 'absolute' as const,
    left: isMobile ? '0.75rem' : '1.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    width: isMobile ? '28px' : '36px',
    height: isMobile ? '28px' : '36px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.05))',
    pointerEvents: 'none' as const,
    zIndex: 1
  }), [isMobile]);

  const inputStyle = useMemo(() => ({
    width: '100%',
    maxWidth: '100%',
    padding: isMobile ? '0.875rem 0.875rem 0.875rem 3.25rem' : '1.25rem 1.25rem 1.25rem 4.5rem',
    border: '2px solid #e5e7eb',
    borderRadius: isMobile ? '12px' : '14px',
    fontSize: isMobile ? '0.9rem' : '1.125rem',
    backgroundColor: 'white',
    color: '#1f2937',
    outline: 'none' as const,
    boxSizing: 'border-box' as const,
    transition: 'all 0.3s ease',
    boxShadow: '0 10px 30px rgba(15, 118, 110, 0.08)',
    overflow: 'hidden' as const
  }), [isMobile]);

  const dropdownStyle = useMemo(() => ({
    position: 'absolute' as const,
    top: 'calc(100% + 0.5rem)', // Position below the input (standard dropdown behavior)
    left: isSmall ? '0.5rem' : isMobile ? '0.75rem' : '0',
    right: isSmall ? '0.5rem' : isMobile ? '0.75rem' : '0',
    background: 'white',
    border: '2px solid #10b981',
    borderRadius: isMobile ? '12px' : '14px',
    boxShadow: '0 16px 60px rgba(15, 118, 110, 0.15)',
    padding: isMobile ? '0.75rem' : '1rem',
    zIndex: 9999, // Very high z-index to ensure it's on top
    maxHeight: isMobile ? '280px' : '320px',
    overflowY: 'auto' as const,
    width: isMobile ? 'calc(100% - 1.5rem)' : 'auto',
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    display: 'block' as const,
    visibility: 'visible' as const,
    opacity: 1
  }), [isMobile, isSmall]);

  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    event.currentTarget.style.borderColor = '#10b981';
    event.currentTarget.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.15)';
    console.log('👁️ Input focused, showing dropdown if there are suggestions or searching');
    if (value.trim() && (suggestions.length > 0 || isSearching)) {
      setShowDropdown(true);
    }
  }, [value, suggestions.length, isSearching]);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    event.currentTarget.style.borderColor = '#e5e7eb';
    event.currentTarget.style.boxShadow = '0 10px 30px rgba(15, 118, 110, 0.08)';
    // Don't close dropdown on blur - let click outside handler do it
    // This prevents dropdown from closing when clicking on it
  }, []);

  return (
    <div
      ref={containerRef}
      style={containerStyle}
    >
      <div
        style={iconContainerStyle}
      >
        <svg
          width={isMobile ? 18 : 22}
          height={isMobile ? 18 : 22}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#047857"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      </div>

      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(event) => handleInputChange(event.target.value)}
        style={inputStyle}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />

      {(() => {
        console.log('🎨 Rendering dropdown check:', {
          shouldShowDropdown,
          showDropdown,
          isSearching,
          suggestionsLength: suggestions.length,
          suggestions: suggestions,
          errorMessage
        });
        return null;
      })()}
      {shouldShowDropdown && (
        <div
          style={dropdownStyle}
          onMouseDown={(e) => {
            // Prevent dropdown from closing when clicking inside it
            e.preventDefault();
          }}
        >
          <div
            style={{
              fontSize: '0.9rem',
              fontWeight: 700,
              color: '#064e3b',
              marginBottom: '0.5rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}
          >
            Available medicines
          </div>

          {isSearching && (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontWeight: 600 }}>
              Searching inventory...
            </div>
          )}

          {!isSearching && errorMessage && (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#dc2626', fontWeight: 600 }}>
              {errorMessage}
            </div>
          )}

          {!isSearching && !errorMessage && suggestions.length === 0 && (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontWeight: 500 }}>
              No medicines found
            </div>
          )}

          {!isSearching && !errorMessage && suggestions.length > 0 && (() => {
            console.log('🎯 Rendering suggestions list, count:', suggestions.length);
            return suggestions.map((suggestion, index) => {
              console.log(`🎯 Rendering suggestion ${index}:`, suggestion);
              const quantityText = suggestion.Quantity
                ? `${suggestion.Quantity} ${normalizeQtyType(suggestion.quantity_type)}`
                : null;
              const totalTablets =
                suggestion.Quantity && suggestion.tablets_per_strip
                  ? Number(suggestion.Quantity) * Number(suggestion.tablets_per_strip)
                  : null;

              return (
                <button
                  key={suggestion.key || `suggestion-${index}`}
                onClick={() => handleSuggestionClick(suggestion)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  border: '1px solid rgba(16, 185, 129, 0.08)',
                  borderRadius: '12px',
                  padding: '0.75rem 1rem',
                  marginBottom: '0.75rem',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(240, 253, 250, 0.8))',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 8px 20px rgba(16, 185, 129, 0.08)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(16, 185, 129, 0.18)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.08)';
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#065f46' }}>
                    {suggestion.Name || 'Unnamed medicine'}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>
                    Manufacturer: <span style={{ color: '#111827', fontWeight: 700 }}>{suggestion.Manufacturer || 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    Composition: <span style={{ color: '#111827' }}>{suggestion.Salt_Composition || 'N/A'}</span>
                  </div>
                  {(quantityText || totalTablets !== null) && (
                    <div style={{ fontSize: '0.8rem', color: '#047857', fontWeight: 600 }}>
                      {quantityText}
                      {totalTablets !== null && (
                        <span style={{ color: '#059669', fontWeight: 500 }}> • {totalTablets} tablets total</span>
                      )}
                    </div>
                  )}
                  {typeof suggestion.fragmented_tablets === 'number' && (
                    <div style={{ fontSize: '0.75rem', color: '#a16207', fontWeight: 600 }}>
                      Fragmented tablets: {suggestion.fragmented_tablets}
                    </div>
                  )}
                </div>
              </button>
            );
            });
          })()}
        </div>
      )}
    </div>
  );
});

SearchbarHomepage.displayName = 'SearchbarHomepage';

export default SearchbarHomepage;

